import { useNavigate } from "react-router-dom";
import useAppState from "../../../context/AppContext";
import { APP_RETIREMENT_URLS, ERROR_MESSAGES } from "../../../const/AppConst";
import StickButtonsBloc from "../../common/StickButtonsBloc";
import { useForm } from "react-hook-form";
import InfoOldContractComponent from "../../../components/info-perso-template/InfoOldContractComponent";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import { useEffect, useState } from "react";
import { IOldCompetitor} from "../../../context/model";
import { getConcurrents } from "../../../services/CustomerApiService";
import { getListOldContracts } from "../../../utils/utils";

export type InfoOldContractBlocFormData = {
    name: string,
    address: string,
    isOtherName: boolean,
    contractNumbers: string,
    buildingName: string,
    city: string,
    postalCode: string,
    otherName?: string,
    additionalAddress?: string,
    hasMgpInsurance?: string,
}

export default function REInfoOldContractBloc() {
  const [concurrents, setConcurrents]= useState<IOldCompetitor[]>([]);

  const { 
    state: { updatedProposition, jwtToken },
    dispatch,
  } = useAppState();
  
  useEffect(()=> {
      const fetchConcurrents: any = async () => {
          const  res= await getConcurrents(jwtToken!.accessToken);
          setConcurrents(res.data);
      };
      fetchConcurrents();
  }, [])


  const CONTRACT_CURRENT_NOMENCLATURES = getListOldContracts(concurrents);
  
  const navigate = useNavigate();
  
  const validationSchema = Yup.object().shape({
      hasMgpInsurance: Yup.string().oneOf(['true', 'false']).required(ERROR_MESSAGES.requiredMsg),
      name: Yup.string()
      .when(['isOtherName', 'hasMgpInsurance'], {
        is: ( isOtherName : boolean, hasMgpInsurance : string) => isOtherName === false && hasMgpInsurance === 'false',
        then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
      }),
      otherName: Yup.string()
      .when('isOtherName', {
        is: true,
        then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
      }),
      address: Yup.string()
        .when('isOtherName', {
          is: true,
          then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        }),
      contractNumbers:  Yup.string()
        .when(['hasMgpInsurance'], {
            is: (hasMgpInsurance : string) => hasMgpInsurance === 'false',
            then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        }),
      city: Yup.string()
      .when('isOtherName', {
        is: true,
        then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
      })
      .max(38, ERROR_MESSAGES.invalidMsg),
      postalCode: Yup.string()
      .when('isOtherName', {
        is: true,
        then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        .matches(/^[0-9]+$/, ERROR_MESSAGES.invalidMsg )
         .max(5, ERROR_MESSAGES.invalidMsg)
         .min(5, ERROR_MESSAGES.invalidMsg)
      }),
      
  });

  let initialContracts: string[]| string|undefined = 
        updatedProposition?.oldCompetitor?.contractNumbers;

  if(initialContracts ) {
      initialContracts = initialContracts?.join(',');
  }

  const {
  register,
  handleSubmit,
  watch,
  setValue,
  formState: { errors },
  } = useForm<InfoOldContractBlocFormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      hasMgpInsurance: updatedProposition?.hasMgpInsurance?.toString(),
      name: updatedProposition?.oldCompetitor?.code ?? '',
      otherName: updatedProposition?.oldCompetitor?.otherName ?? '',
      address: updatedProposition?.oldCompetitor?.address ?? '',
      buildingName: updatedProposition?.oldCompetitor?.buildingName ?? "",
      contractNumbers: initialContracts ??'',
      isOtherName: updatedProposition?.oldCompetitor?.isOtherName ?? false ,
      city: updatedProposition?.oldCompetitor?.city,
      postalCode: updatedProposition?.oldCompetitor?.postalCode,
      additionalAddress: updatedProposition?.oldCompetitor?.additionalAddress,
    },
    resolver: yupResolver(validationSchema),
  });
  const onNextAction = async (data: InfoOldContractBlocFormData) => {
    let tempUpdatedPropo = updatedProposition;  
    let result : IOldCompetitor|undefined;
    
    // split props hasMgpInsurance to avoid duplicate hasMgpInsurance in oldCompetitor in updatedProposition
    const {hasMgpInsurance, ...oldCompetitorData} = data;

    if(!data.isOtherName) {
      result = concurrents
        .find(item=> item.code === data.name);

      if(result){ 
        result.name = CONTRACT_CURRENT_NOMENCLATURES
          ?.find(item=> item.code === data.name)?.label;
        result.isOtherName = false;
        result.contractNumbers = data.contractNumbers.split(',');
      }
    }

    if(data.isOtherName){
      result={...oldCompetitorData, 
        contractNumbers: data.contractNumbers.split(','), 
        code: '',
      }
    }

    if(tempUpdatedPropo) { 
      tempUpdatedPropo = { 
        ...tempUpdatedPropo, 
        hasMgpInsurance : hasMgpInsurance === "true",
        oldCompetitor: {
          ...tempUpdatedPropo.oldCompetitor, 
          ...result} 
      }
    }
    const nextUrl = APP_RETIREMENT_URLS.infoBankPage;

    dispatch({
        type: 'NEXT_INFO_STEP', 
        path: nextUrl, 
        updatedProposition: tempUpdatedPropo
    });

    navigate({
        pathname: nextUrl
    });         
  }

  const onPreviousAction = async () => {
    const url = APP_RETIREMENT_URLS.infoContactPage;
   
    dispatch({type: 'BACK_INFO_STEP', path: url});
    navigate({
        pathname: url
    });         
  }

  return (
    <form 
        style={{width: '100%'}} 
        className='bg-blanc shadow-noir p-lg mw-100'>
        
      <InfoOldContractComponent 
          register={register} 
          errors={errors} 
          watch={watch} 
          setValue={setValue}
          concurrentOptions={CONTRACT_CURRENT_NOMENCLATURES}
          isRetired={true}
      />
        
      <StickButtonsBloc 
          showPreviousButton={true} 
          disabledPreviousButton={false} 
          nextAction={handleSubmit(onNextAction)} 
          showNextButton={true} 
          previousAction={onPreviousAction}
        />
    </form>
  )
}
